import React, { useState, useEffect } from 'react';
import { Utensils, Calendar, Clock, MapPin, Check, X, Loader, AlertCircle, User, ChefHat, Beer } from 'lucide-react';
import { getPendingDinnerRecommendations, updateDinnerRecommendation, DinnerRecommendation } from '../lib/dinnerRecommendations';

const DinnerRecommendations: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<DinnerRecommendation[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        setLoading(true);
        setError(null);
        const pendingRecommendations = await getPendingDinnerRecommendations();
        setRecommendations(pendingRecommendations);
        break; // Success, exit the retry loop
      } catch (err) {
        console.error(`Attempt ${attempt + 1} failed:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load dinner recommendations');
        attempt++;
        
        if (attempt < maxRetries) {
          // Wait before retrying with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
        }
      }
    }
    
    setLoading(false);
  };

  const handleAction = async (recommendationId: string, status: 'accepted' | 'rejected') => {
    try {
      setActionLoading(recommendationId);
      setError(null);
      
      await updateDinnerRecommendation(recommendationId, status);
      
      // Remove the recommendation from the list
      setRecommendations(prev => prev.filter(rec => rec.id !== recommendationId));
    } catch (err) {
      console.error(`Error ${status === 'accepted' ? 'accepting' : 'rejecting'} dinner recommendation:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${status === 'accepted' ? 'accept' : 'reject'} dinner recommendation`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Utensils className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p>Keine ausstehenden Dinner Vorschläge</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-700">Pending Dinner Recommendations</h3>
      
      {recommendations.map((recommendation) => (
        <div key={recommendation.id} className="bg-white rounded-lg shadow-sm p-4 border border-green-100">
          <div className="flex items-start space-x-3 mb-3">
            {recommendation.sender?.profile_picture ? (
              <img
                src={recommendation.sender.profile_picture}
                alt={recommendation.sender.full_name || ''}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-400" />
              </div>
            )}
            <div className="flex-1">
              <p className="font-medium">{recommendation.sender?.full_name} wants to have dinner with you!</p>
              <p className="text-gray-600 mt-1">{recommendation.message}</p>
            </div>
          </div>

          {(recommendation.date || recommendation.time || recommendation.restaurant || recommendation.bar) && (
            <div className="bg-green-50 rounded-md p-3 mb-3">
              {(recommendation.date || recommendation.time) && (
                <div className="flex items-center space-x-2 text-gray-700 mb-2">
                  {recommendation.date && (
                    <>
                      <Calendar className="h-4 w-4 text-green-600" />
                      <span>{new Date(recommendation.date).toLocaleDateString()}</span>
                    </>
                  )}
                  {recommendation.time && (
                    <>
                      {recommendation.date && <span>•</span>}
                      <Clock className="h-4 w-4 text-green-600" />
                      <span>{recommendation.time.slice(0, 5)}</span>
                    </>
                  )}
                </div>
              )}

              {(recommendation.restaurant || recommendation.bar) && (
                <div className="flex items-center space-x-2 text-gray-700">
                  {recommendation.restaurant ? (
                    <>
                      <ChefHat className="h-4 w-4 text-green-600" />
                      <span>{recommendation.restaurant.name}</span>
                    </>
                  ) : recommendation.bar ? (
                    <>
                      <Beer className="h-4 w-4 text-green-600" />
                      <span>{recommendation.bar.name}</span>
                    </>
                  ) : null}
                  
                  {(recommendation.restaurant?.address || recommendation.bar?.address) && (
                    <>
                      <span>•</span>
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span>{recommendation.restaurant?.address || recommendation.bar?.address}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => handleAction(recommendation.id, 'rejected')}
              disabled={!!actionLoading}
              className={`px-3 py-1 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition flex items-center space-x-1 ${
                actionLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {actionLoading === recommendation.id ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              <span>Decline</span>
            </button>
            <button
              onClick={() => handleAction(recommendation.id, 'accepted')}
              disabled={!!actionLoading}
              className={`px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition flex items-center space-x-1 ${
                actionLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {actionLoading === recommendation.id ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              <span>Accept</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DinnerRecommendations;